import * as React from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from 'lib/utils'

interface TablePaginationProps {
	page: number
	rowsPerPage: number
	count: number
	onPageChange: (page: number) => void
	onRowsPerPageChange: (rowsPerPage: number) => void
	rowsPerPageOptions?: number[]
	className?: string
}

const TablePagination = ({
	page,
	rowsPerPage,
	count,
	onPageChange,
	onRowsPerPageChange,
	rowsPerPageOptions = [10, 15, 25],
	className,
}: TablePaginationProps) => {
	const totalPages = Math.ceil(count / rowsPerPage)
	const from = count === 0 ? 0 : page * rowsPerPage + 1
	const to = Math.min((page + 1) * rowsPerPage, count)

	return (
		<div className={cn('flex items-center justify-end gap-6 py-3 px-2 text-sm text-gray-600', className)}>
			<div className="flex items-center gap-2">
				<span className="text-xs">Rows per page:</span>
				<select
					value={rowsPerPage}
					onChange={e => {
						onRowsPerPageChange(Number(e.target.value))
						onPageChange(0)
					}}
					className="h-8 rounded border border-gray-300 bg-white px-2 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
				>
					{rowsPerPageOptions.map(opt => (
						<option key={opt} value={opt}>
							{opt}
						</option>
					))}
				</select>
			</div>

			<span className="text-xs min-w-[80px] text-center">
				{from}–{to} of {count}
			</span>

			<div className="flex items-center gap-1">
				<button
					type="button"
					onClick={() => onPageChange(page - 1)}
					disabled={page === 0}
					className="inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
					aria-label="Previous page"
				>
					<ChevronLeft className="h-3 w-3" />
				</button>
				<button
					type="button"
					onClick={() => onPageChange(page + 1)}
					disabled={page >= totalPages - 1}
					className="inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
					aria-label="Next page"
				>
					<ChevronRight className="h-3 w-3" />
				</button>
			</div>
		</div>
	)
}
TablePagination.displayName = 'TablePagination'

export { TablePagination }
export type { TablePaginationProps }
